import { BaseApiRequest } from '@api/BaseApi';
import moment from 'moment-timezone';
import { TIMESTAMP, WARNINGS_TYPE } from '@constants/index';
import { toast } from 'react-toastify';
import { getOffset } from '@api/helpers';
import { logError } from './Logging';
import { isMissingTokenError } from './apiErrors';

const setEndDateOffset = (endDate, offset) => {
  return `${moment.parseZone(endDate).format(TIMESTAMP)}${offset}`;
};

export const fetchPowerMgmtData = (signal, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/PowerMgmt?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Power mgmt: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchOverviewKeyFiguresData = (
  signal,
  { selectedRig, startDate, endDate },
  rollup
) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/RigOverviewKeyFigures?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Overview Key Figures: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchOverviewKeyFiguresDataV2 = (
  signal,
  { selectedRig, startDate, endDate },
  rollup
) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/RigOverviewKeyFigures?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Overview Key Figures: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchOverviewData = (signal, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/RigOverview?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Overview: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchEquipmentData = (signal, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/Equipment/GetAllComponentConsumptionsAggregated?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Equipment: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchSignalsData = (signal, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/Equipment/GetAllComponentConsumptions?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Equipment: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
}

export const fetchEquipmentSignalsData = (signal, equipmentName, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  // encodeURIComponent because equipment/sub-category names are free text from the rig
  // configuration and some contain a slash - "XFMR/Feeder" on Tungsten Explorer. Interpolated
  // raw, that slash became a path separator, so the request went to
  // /Equipment/XFMR/Feeder/GetSingleComponentConsumption, matched no route, and returned 404.
  // The API handles the encoded form correctly (verified: %2F returns 200).
  return BaseApiRequest(signal, `/Equipment/${encodeURIComponent(equipmentName)}/GetSingleComponentConsumption?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Equipment: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
}

export const fetchEquipmentConfigurations = (signal, rigId) => {
  return BaseApiRequest(
    signal, 
    `/Equipment/${rigId}/GetRigEquipmentConfigurations`, 
    'gpr', 
    'GET'
    ).catch((error) => {
      if (!signal?.aborted && !isMissingTokenError(error)) {
        toast.error(`Equipment Configurations: ${error.errors[0]}`, {
          position: 'top-right'
        });
      }
  });
}

export const fetchRigEquipments = (signal, rigId) => {
  return BaseApiRequest(
    signal, 
    `/Equipment/${rigId}/GetRigEquipments`, 
    'gpr', 
    'GET'
    ).catch((error) => {
      if (!signal?.aborted && !isMissingTokenError(error)) {
        toast.error(`Retrieving Rig Equipments: ${error.errors[0]}`, {
          position: 'top-right'
        });
      }
  });
}

export const fetchDefinedFocusAreas = async (signal, selectedRig) => {
    return await BaseApiRequest(
      signal,
      `/Analytics/${selectedRig.rigId}/GetRigDefinedFocusAreas`,
      'gpr',
      'GET'
      ).catch ((error) => {
    if (!signal?.aborted) {
      if (!isMissingTokenError(error)) {
        toast.error(`Could not retrieve Focus Areas for Rig: ${selectedRig.rigName}.`,
          {
            position: 'top-right'
          });
      }
      logError(error, `Failer to retrieve FocusAreas for RigId: ${selectedRig.rigId}, RigName: ${selectedRig.rigName}.`);
      }
  });
}

export const fetchDrillingActivitiesData = (
  signal,
  { selectedRig, startDate, endDate },
  rollup
) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/DrillingActivities?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Drilling activities: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};
export const fetchDrillingActivitiesV2Data = (
  signal,
  { selectedRig, startDate, endDate },
  rollup
) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/DrillingActivities?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Drilling activities: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};

export const fetchAnalyticsData = (signal, { selectedRig, startDate, endDate }, rollup) => {
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/Analytics?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted) {
      if (!isMissingTokenError(error)) {
        toast.error(`Analytics: ${error.errors[0]}`, {
          position: 'top-right'
        });
      }
      return [];
    }
  });
};

export const fetchWarningData = (
  signal,
  selectedRig,
  startDate,
  endDate,
  focusAreaStartDate,
  focusAreaEndDate,
  rollup,
  type = WARNINGS_TYPE.TooManyHPU
) => {
  let uri = '';
  switch(type) {
    case WARNINGS_TYPE.TooManyHPU:
      uri = 'HPUFocusArea';
      break;
    case WARNINGS_TYPE.TooManyEng:
      uri = 'EngineFocusArea';
      break;
    case WARNINGS_TYPE.TooManyAgitators:
      uri = 'MudTreatmentFocusArea';
      break;

  }
  const queryParams = new URLSearchParams({
    rigName: selectedRig.rigName,
    rigId: selectedRig.rigId,
    startDate: startDate,
    endDate: setEndDateOffset(endDate, getOffset(startDate)),
    focusAreaStartDate: `${focusAreaStartDate}${getOffset(startDate)}`,
    focusAreaEndDate: `${focusAreaEndDate}${getOffset(startDate)}`,
    rollup: rollup,
    offset: getOffset(startDate)
  });
  return BaseApiRequest(signal, `/${uri}?${queryParams.toString()}`, 'gpr', 'GET').catch((error) => {
    if (!signal?.aborted && !isMissingTokenError(error)) {
      toast.error(`Warning ${uri}: ${error.errors[0]}`, {
        position: 'top-right'
      });
    }
  });
};
